import { FormEvent, useState } from 'react';
import { updateAircraftFuel } from '../../api';
import { useAircraft } from '../../aircraft/AircraftContext';
import { useFeedback } from '../../hooks/useFeedback';
import { getApiErrorMessage } from '../../utils/apiError';
import '../modal/ModalBase.css';
import ModalOverlay from '../modal/ModalOverlay';
import ModalPortal from '../modal/ModalPortal';
import './EditAvgFuelModal.css';

type Props = {
  avgFuelBurn: number | null;
  fuelPrice: number | null;
  onClose: () => void;
  onSaved: (next: {
    avg_fuel_burn_lph: number | null;
    fuel_price_per_liter: number | null;
  }) => void;
};

const parseOptionalNumber = (raw: string) => {
  const trimmed = raw.trim().replace(',', '.');
  if (trimmed === '') return null;
  return Number(trimmed);
};

const EditAvgFuelModal = ({
  avgFuelBurn,
  fuelPrice,
  onClose,
  onSaved,
}: Props) => {
  const { refreshList } = useAircraft();
  const { showError, feedbackNode } = useFeedback();
  const [burn, setBurn] = useState(
    avgFuelBurn != null ? String(avgFuelBurn) : ''
  );
  const [price, setPrice] = useState(
    fuelPrice != null ? String(fuelPrice) : ''
  );
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const burnValue = parseOptionalNumber(burn);
    const priceValue = parseOptionalNumber(price);
    if (
      (burnValue != null && (!Number.isFinite(burnValue) || burnValue < 0)) ||
      (priceValue != null && (!Number.isFinite(priceValue) || priceValue < 0))
    ) {
      showError('Укажите корректные неотрицательные значения');
      return;
    }

    setSaving(true);
    try {
      const res = await updateAircraftFuel({
        avg_fuel_burn_lph: burnValue,
        fuel_price_per_liter: priceValue,
      });
      await refreshList();
      window.dispatchEvent(new Event('aircraft-updated'));
      onSaved({
        avg_fuel_burn_lph: res.data.avg_fuel_burn_lph ?? burnValue,
        fuel_price_per_liter: res.data.fuel_price_per_liter ?? priceValue,
      });
      onClose();
    } catch (error) {
      showError(getApiErrorMessage(error, 'Ошибка сохранения'));
    }
    setSaving(false);
  };

  return (
    <ModalPortal onClose={saving ? undefined : onClose}>
      <ModalOverlay onClose={saving ? undefined : onClose}>
        {feedbackNode}
        <form
          className="modal edit-avg-fuel-modal"
          onClick={(e) => e.stopPropagation()}
          onSubmit={(e) => void handleSubmit(e)}
        >
          <h3>Установленное среднее значение расхода топлива</h3>
          <div className="modal-body">
            <label>
              Расход, л/ч
              <input
                type="text"
                inputMode="decimal"
                autoComplete="off"
                value={burn}
                onChange={(e) => setBurn(e.target.value)}
                placeholder="0"
                disabled={saving}
                aria-label="Установленное среднее значение расхода топлива"
              />
            </label>
            <label>
              Стоимость топлива, ₽/л
              <input
                type="text"
                inputMode="decimal"
                autoComplete="off"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="0"
                disabled={saving}
                aria-label="Стоимость топлива"
              />
            </label>
          </div>
          <div className="modal-actions">
            <button
              type="button"
              className="btn-cancel"
              disabled={saving}
              onClick={onClose}
            >
              Отмена
            </button>
            <button type="submit" className="btn-save" disabled={saving}>
              {saving ? 'Сохранение...' : 'Сохранить'}
            </button>
          </div>
        </form>
      </ModalOverlay>
    </ModalPortal>
  );
};

export default EditAvgFuelModal;
